import React, { useMemo, useState } from 'react';
import { CloudDownload } from 'lucide-react';
import toolkitService from '../services/ToolkitService';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitFileStep from './toolkit/ToolkitFileStep';
import ToolkitBatchTable from './toolkit/ToolkitBatchTable';
import ToolkitMappingEditor from './toolkit/ToolkitMappingEditor';
import useStoredRows from './toolkit/useStoredRows';
import { sourceRow } from './toolkit/useToolkitBatch';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › Block Grid Update — ported from the desktop's Scripts › Block Grid Update. For an Opera
// Cloud → Opera Cloud project: each block's room allocation grid is read from the SOURCE property and written to the
// matching block at the TARGET (toolkit groups.js process-block), room types translated through the mapping below.
//
//   1. Room type mappings — loaded from the target's DVM (RoomCategoryLabel conversions) and/or edited by hand; saved
//      per property. A block whose rooms include a room type with no mapping is stopped, naming it, rather than written
//      with part of its grid missing (the desktop dropped those rooms and still reported success).
//   2. Block mappings — a CSV / Excel file of source block id, source code, target code, [target id]. The target id is
//      looked up by code when missing, and kept.
//   3. Process — cancelled, lost and refused blocks are skipped. Rows and results are kept, so a run resumes.

const COLUMNS = [
    { key: 'sourceBlockId', label: 'Source block ID' },
    { key: 'sourceBlockCode', label: 'Source code' },
    { key: 'targetBlockCode', label: 'Target code' },
    { key: 'targetBlockId', label: 'Target block ID' },
    { key: 'result', label: 'Result' },
];

function ToolkitBlockGridPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const [roomTypes, setRoomTypes] = useState({});
    const [dvm, setDvm] = useState(null);
    const [dvmMsg, setDvmMsg] = useState('');
    const map = useMemo(() => roomTypes.ROOM_TYPE || {}, [roomTypes]);

    const run = useMemo(() => ({
        request: (row) => ({ area: 'groups', op: 'process-block', args: { row: sourceRow(row, ['steps']), roomTypeMap: map } }),
        server: { screen: 'block-grid', title: 'Block Grid Update', keyOf: (row) => row.rowId },
    }), [map]);
    const { batch, replace, message } = useStoredRows(ctx, ready, 'block-grid', run);

    const loadDvm = async () => {
        setDvmMsg('Loading room type conversions from the target DVM…');
        try {
            const { mappings } = (await toolkitService.groups(ctx, 'fetch-dvm-room-types')).data.result;
            setDvm({ category: 'ROOM_TYPE', mappings });
            setDvmMsg(`${Object.keys(mappings).length} conversion(s) loaded — review, then Save mappings.`);
        } catch (err) {
            setDvmMsg(err?.response?.data?.message || err.message);
        }
    };

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="Block Grid Update"
                subtitle="Copy block room allocations from the source Opera Cloud to the target, block by block." />

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">1</span> Room type mappings (source → target)</div>
                <ToolkitMappingEditor ctx={ctx} ready={ready} configKey="block-grid-room-types"
                    categories={[{ key: 'ROOM_TYPE', label: 'Room types' }]} onChange={setRoomTypes} external={dvm}
                    extra={<Button variant="secondary" onClick={loadDvm} disabled={!ready}><CloudDownload className="icon" /> Load from target DVM</Button>} />
                {dvmMsg && <p className="tkb-msg">{dvmMsg}</p>}
            </div>

            <ToolkitFileStep number={2} title="Block mappings" label="Choose the block mapping file (CSV or .xlsx)"
                accept=".csv,.xlsx,.txt" kind="block-mappings" onParsed={replace} disabled={!ready}>
                <p className="tkb-hint">Columns: source block ID, source block code, target block code, target block ID (optional — looked up by code).</p>
                {message && <p className="tkb-msg">{message}</p>}
            </ToolkitFileStep>

            <ToolkitBatchTable batch={batch} columns={COLUMNS} searchKeys={['sourceBlockId', 'sourceBlockCode', 'targetBlockCode']}
                exportName="Block_Grid_Update" canRun={ready && Object.keys(map).length > 0} onClear={() => replace([])} />
        </div>
    );
}

export default ToolkitBlockGridPage;
